<template>
  <div class="animated fadeInRight inspection-plan-container">
    <!-- (1) PageHeader -->
    <ds-page-header :title="$t('inspectionPlan.page.title')" :subtitle="$t('inspectionPlan.page.subtitle')" />

    <!-- (2) SearchPanel -->
    <ds-search-panel>
      <el-row :gutter="16">
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model.trim="searchConditions.inspectionPlanId" clearable
            :placeholder="$t('inspectionPlan.inspectionPlanIdPlaceholder')" @keyup.enter.native="fetchInspectionPlans" />
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model.trim="searchConditions.inspectionPlanName" clearable
            :placeholder="$t('inspectionPlan.inspectionPlanNamePlaceholder')" @keyup.enter.native="fetchInspectionPlans" />
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model.trim="searchConditions.staffNameLike" clearable
            :placeholder="$t('inspectionPlan.staffNamePlaceholder')" @keyup.enter.native="fetchInspectionPlans" />
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-select v-model="searchConditions.state" clearable class="w-full"
            :placeholder="$t('inspectionPlan.statePlaceholder')">
            <el-option v-for="(item, index) in stateOptions" :key="index" :label="item.name" :value="item.statusCd" />
          </el-select>
        </el-col>
      </el-row>

      <template #actions>
        <el-button type="primary" icon="el-icon-search" @click="fetchInspectionPlans">
          {{ $t('inspectionPlan.query') }}
        </el-button>
        <el-button icon="el-icon-refresh-left" @click="resetSearch">
          {{ $t('inspectionPlan.reset') }}
        </el-button>
      </template>
    </ds-search-panel>

    <!-- (3)(4)(6) Toolbar + DataTable + PaginationBar -->
    <div class="ds-panel">
      <ds-toolbar :total="pagination.total" :density="density" :show-column-setting="false" :show-refresh="true"
        @update:density="density = $event" @refresh="fetchInspectionPlans">
        <template #left>
          <span class="ds-panel__title">{{ $t('inspectionPlan.inspectionPlanTitle') }}</span>
        </template>
        <template #right>
          <el-button type="primary" size="mini" icon="el-icon-plus" @click="openAddModal">
            {{ $t('inspectionPlan.add') }}
          </el-button>
        </template>
      </ds-toolbar>

      <ds-data-table :data="planList" :loading="loading" :error="hasError" :error-message="errorMessage"
        :density="density" border :empty-description="$t('inspectionPlan.page.empty')" @retry="fetchInspectionPlans">
        <el-table-column prop="inspectionPlanName" :label="$t('inspectionPlan.planName')" align="left" min-width="160"
          show-overflow-tooltip />
        <el-table-column prop="inspectionRouteName" :label="$t('inspectionPlan.planRoute')" align="center" min-width="140"
          show-overflow-tooltip />
        <el-table-column prop="inspectionPlanPeriodName" :label="$t('inspectionPlan.planCycle')" align="center"
          width="110" />
        <el-table-column prop="signTypeName" :label="$t('inspectionPlan.signMethod')" align="center" width="100" />
        <!-- 日期范围：起止两行 -->
        <el-table-column :label="$t('inspectionPlan.dateRange')" align="center" width="170">
          <template slot-scope="scope">
            <div class="ds-num">{{ scope.row.startDate || '-' }}</div>
            <div class="ds-num ds-cell-sub">{{ scope.row.endDate || '-' }}</div>
          </template>
        </el-table-column>
        <!-- 时间范围：起止两行 -->
        <el-table-column :label="$t('inspectionPlan.timeRange')" align="center" width="150">
          <template slot-scope="scope">
            <div class="ds-num">{{ scope.row.startTime || '-' }}</div>
            <div class="ds-num ds-cell-sub">{{ scope.row.endTime || '-' }}</div>
          </template>
        </el-table-column>
        <el-table-column prop="beforeTime" :label="$t('inspectionPlan.taskAdvance')" align="right" width="110">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.beforeTime != null ? scope.row.beforeTime : '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="createUserName" :label="$t('inspectionPlan.maker')" align="center" width="100" />
        <el-table-column prop="createTime" :label="$t('inspectionPlan.createTime')" align="center" width="160" />
        <!-- 状态 Tag：颜色仅作辅助，始终带文字 -->
        <el-table-column prop="stateName" :label="$t('inspectionPlan.status')" align="center" width="110">
          <template slot-scope="scope">
            <ds-status-tag :type="resolveState(scope.row)" :label="scope.row.stateName || '-'" />
          </template>
        </el-table-column>
        <!-- 巡检人员：多人纵向排列 -->
        <el-table-column :label="$t('inspectionPlan.inspectionStaff')" align="center" min-width="120">
          <template slot-scope="scope">
            <div v-if="scope.row.staffs && scope.row.staffs.length">
              <div v-for="(staff, i) in scope.row.staffs" :key="i">{{ staff.staffName }}</div>
            </div>
            <span v-else class="ds-cell-sub">-</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('inspectionPlan.operation')" align="center" width="260" fixed="right">
          <template slot-scope="scope">
            <el-button size="mini" type="text" @click="openEditModal(scope.row)">
              {{ $t('inspectionPlan.modify') }}
            </el-button>
            <el-button size="mini" type="text" class="ds-btn-danger" @click="openDeleteModal(scope.row)">
              {{ $t('inspectionPlan.delete') }}
            </el-button>
            <el-button v-if="scope.row.state === '2020025'" size="mini" type="text"
              @click="openStateModal(scope.row, '2020026', $t('inspectionPlan.disable'))">
              {{ $t('inspectionPlan.disable') }}
            </el-button>
            <el-button v-else size="mini" type="text"
              @click="openStateModal(scope.row, '2020025', $t('inspectionPlan.enable'))">
              {{ $t('inspectionPlan.enable') }}
            </el-button>
            <el-button size="mini" type="text" @click="viewDetail(scope.row)">
              {{ $t('inspectionPlan.detail') }}
            </el-button>
          </template>
        </el-table-column>
      </ds-data-table>

      <ds-pagination-bar :total="pagination.total" :page="pagination.current" :size="pagination.size"
        :page-sizes="[10, 20, 30, 50]" layout="total, sizes, prev, pager, next" @size-change="handleSizeChange"
        @current-change="handlePageChange">
        <template #summary>
          <span class="ds-tip-text">{{ $t('inspectionPlan.note') }}</span>
        </template>
      </ds-pagination-bar>
    </div>

    <edit-inspection-plan ref="editModal" @success="handleSuccess" />
    <delete-inspection-plan ref="deleteModal" @success="handleSuccess" />
    <inspection-plan-state ref="stateModal" @success="handleSuccess" />
  </div>
</template>

<script>
import { listInspectionPlans } from '@/api/inspection/inspectionPlanApi'
import EditInspectionPlan from '@/components/inspection/editInspectionPlan'
import DeleteInspectionPlan from '@/components/inspection/deleteInspectionPlan'
import InspectionPlanState from '@/components/inspection/inspectionPlanState'
import { getDict } from '@/api/community/communityApi'
import { resolveStatus } from '@/utils/statusMap'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsSearchPanel from '@/components/ds/ds-search-panel.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsStatusTag from '@/components/ds/ds-status-tag.vue'
import DsPaginationBar from '@/components/ds/ds-pagination-bar.vue'

export default {
  name: 'InspectionPlanList',
  components: {
    EditInspectionPlan,
    DeleteInspectionPlan,
    InspectionPlanState,
    DsPageHeader,
    DsSearchPanel,
    DsToolbar,
    DsDataTable,
    DsStatusTag,
    DsPaginationBar
  },
  data() {
    return {
      loading: false,
      hasError: false,
      errorMessage: '',
      density: 'default',
      searchConditions: {
        inspectionPlanId: '',
        inspectionPlanName: '',
        staffNameLike: '',
        state: ''
      },
      planList: [],
      stateOptions: [],
      pagination: {
        current: 1,
        size: 10,
        total: 0
      }
    }
  },
  async created() {
    await this.loadDictData()
    this.fetchInspectionPlans()
  },
  methods: {
    async loadDictData() {
      try {
        const res = await getDict('inspection_plan', 'state')
        this.stateOptions = Array.isArray(res) ? res : []
      } catch (error) {
        console.error('Failed to load dictionary data:', error)
      }
    },

    async fetchInspectionPlans() {
      try {
        this.loading = true
        this.hasError = false
        this.errorMessage = ''
        const params = {
          ...this.searchConditions,
          page: this.pagination.current,
          row: this.pagination.size
        }

        const res = await listInspectionPlans(params)

        // 兼容后端在「无匹配数据」时返回 { code:0, records:0 } 而不带 data 字段的情况。
        this.planList = Array.isArray(res.data) ? res.data : []
        this.pagination.total = res.total || 0
      } catch (error) {
        this.hasError = true
        this.errorMessage = this.$t('inspectionPlan.page.loadFailed')
        this.$message.error(this.$t('inspectionPlan.fetchError'))
      } finally {
        this.loading = false
      }
    },

    resetSearch() {
      this.searchConditions = {
        inspectionPlanId: '',
        inspectionPlanName: '',
        staffNameLike: '',
        state: ''
      }
      this.pagination.current = 1
      this.fetchInspectionPlans()
    },

    handleSizeChange(size) {
      this.pagination.size = size
      this.pagination.current = 1
      this.fetchInspectionPlans()
    },

    handlePageChange(page) {
      this.pagination.current = page
      this.fetchInspectionPlans()
    },

    openAddModal() {
      this.$router.push({ path: '/views/inspection/addInspectionPlan' })
    },

    openEditModal(plan) {
      this.$refs.editModal.open(plan)
    },

    openDeleteModal(plan) {
      this.$refs.deleteModal.open(plan)
    },

    openStateModal(plan, state, stateName) {
      this.$refs.stateModal.open({
        inspectionPlanId: plan.inspectionPlanId,
        state,
        stateName
      })
    },

    viewDetail(plan) {
      window.open(`/#/pages/inspection/inspectionPlanDetail?inspectionPlanId=${plan.inspectionPlanId}`)
    },

    handleSuccess() {
      this.fetchInspectionPlans()
    },

    /**
     * 状态语义类型（巡检计划），未命中时按文案语义推断并安全降级。
     */
    resolveState(row) {
      if (!row) return 'muted'
      return resolveStatus(row.state, {
        domain: 'inspectionPlan',
        text: row.stateName || ''
      }).type
    }
  }
}
</script>

<style lang="scss" scoped>
.inspection-plan-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

/* 表格 + 分页 组成的白色面板 */
.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;

  &__title {
    font-size: var(--ds-font-size-lg);
    font-weight: 600;
    color: var(--ds-gray-800);
  }
}

/* 表格次要文字 */
.ds-cell-sub {
  font-size: var(--ds-font-size-xs);
  color: var(--ds-gray-500);
}

/* 分页条左侧提示文案 */
.ds-tip-text {
  font-size: var(--ds-font-size-xs);
  color: var(--ds-gray-500);
  line-height: 1.6;
}

/* 危险操作文字按钮 */
.ds-btn-danger {
  color: var(--ds-danger) !important;
}
</style>
